/**
 * Per-row indicator on the TreatmentPlans list. Renders a self-describing
 * pill — colored dot + plain-language label — so a glance tells the user
 * what's happening for that patient. Click opens <PatientScrapeJobsModal />.
 *
 * States (highest precedence first):
 *   - awaiting_2fa     "2FA needed"           (gold,  needs attention)
 *   - active           "Fetching insurance"   (purple, animated)
 *   - succeeded recent "Insurance verified"   (green)
 *   - failed recent    "Couldn't verify"      (red)
 *   - none             render nothing
 *
 * react-redux 6.x — uses connect().
 */

import React from "react";
import { connect } from "react-redux";
import { Tooltip } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  KeyOutlined,
} from "@ant-design/icons";

import PatientScrapeJobsModal from "./PatientScrapeJobsModal";
import { debugLog } from "../../utils/debugLog";
import "./ScrapeJobsIndicator.css";

const ACTIVE_STATES = new Set(["queued", "claimed", "running", "awaiting_2fa"]);
const TERMINAL_STATES = new Set(["succeeded", "failed", "cancelled", "timed_out"]);
const RECENT_WINDOW_MS = 24 * 60 * 60 * 1000;

function carrierShort(job) {
  // Carrier codes are short already (SUNLIFE, MANULIFE, ...). Title-case for display.
  if (!job || !job.carrier_code) return "";
  const c = String(job.carrier_code).toLowerCase();
  return c.charAt(0).toUpperCase() + c.slice(1);
}

class TreatmentCardScrapeJobIndicator extends React.Component {
  state = { open: false };

  componentDidUpdate(prevProps) {
    const prevHead = prevProps.jobs && prevProps.jobs[0];
    const head = this.props.jobs && this.props.jobs[0];
    if (head && (!prevHead || prevHead.state !== head.state || prevHead.job_id !== head.job_id)) {
      debugLog.info("indicator", "indicator_state_change", {
        patient_id: this.props.patientId,
        job_id: head.job_id,
        state: head.state,
      });
    }
  }

  handleClick = (e) => {
    e.stopPropagation();
    this.setState({ open: true });
  };

  handleClose = () => this.setState({ open: false });

  pickPill() {
    const { jobs } = this.props;
    if (!jobs || jobs.length === 0) return null;

    const active = jobs.filter(function (j) { return ACTIVE_STATES.has(j.state); });
    const awaiting2fa = active.filter(function (j) { return j.state === "awaiting_2fa"; });

    if (awaiting2fa.length > 0) {
      const label = awaiting2fa.length === 1
        ? "2FA needed"
        : awaiting2fa.length + " need 2FA";
      return {
        variant: "2fa",
        leading: <KeyOutlined />,
        animated: false,
        label: label,
        count: 0,
        tooltip: "An insurance fetch is waiting for a 2FA code from the carrier. Click to enter it.",
      };
    }
    if (active.length > 0) {
      const head = active[0];
      const carrier = carrierShort(head);
      const label = active.length > 1
        ? "Fetching insurance"
        : (carrier ? "Fetching " + carrier : "Fetching insurance");
      return {
        variant: "running",
        leading: null,
        animated: true,
        label: label,
        count: active.length > 1 ? active.length : 0,
        tooltip: active.length === 1
          ? "Looking up insurance coverage with the carrier — usually 1–3 minutes."
          : active.length + " insurance fetches in progress for this patient.",
      };
    }

    // No active. Look at most recent terminal within the last 24h.
    const cutoff = Date.now() - RECENT_WINDOW_MS;
    const recentTerminal = jobs
      .filter(function (j) { return TERMINAL_STATES.has(j.state); })
      .filter(function (j) {
        const ts = j.completed_at || j.updated_at;
        if (!ts) return false;
        return new Date(ts).getTime() >= cutoff;
      })
      .sort(function (a, b) {
        const ta = a.completed_at || a.updated_at || "";
        const tb = b.completed_at || b.updated_at || "";
        return tb.localeCompare(ta);
      })[0];

    if (!recentTerminal) return null;

    if (recentTerminal.state === "succeeded") {
      return {
        variant: "success",
        leading: <CheckOutlined />,
        animated: false,
        label: "Insurance verified",
        count: 0,
        tooltip: "Latest insurance fetch completed successfully. Click to see details.",
      };
    }
    if (recentTerminal.state === "failed" || recentTerminal.state === "timed_out") {
      const reason = recentTerminal.error_message
        ? recentTerminal.error_message
        : (recentTerminal.state === "timed_out" ? "The fetch timed out." : "The fetch failed.");
      return {
        variant: "failed",
        leading: <CloseOutlined />,
        animated: false,
        label: "Couldn't verify",
        count: 0,
        tooltip: reason,
      };
    }
    return null;
  }

  render() {
    const pill = this.pickPill();
    if (!pill) return null;

    const className = "scrape-pill scrape-pill-" + pill.variant;
    return (
      <React.Fragment>
        {/* The anchor reserves the collapsed footprint in the flex flow.
         * The pill is positioned absolutely inside the anchor and grows
         * leftward on hover (see ScrapeJobsIndicator.css), so it never
         * pushes against or overlaps the Policy button to its right. */}
        <span className="scrape-pill-anchor">
          <Tooltip title={pill.tooltip} mouseEnterDelay={0.15}>
            <button
              type="button"
              className={className}
              onClick={this.handleClick}
              aria-label={pill.label}
            >
              {pill.animated ? (
                <span className="scrape-pill-dot" aria-hidden="true">
                  <span className="scrape-pill-dot-ring" />
                  <span className="scrape-pill-dot-core" />
                </span>
              ) : (
                <span className="scrape-pill-icon" aria-hidden="true">{pill.leading}</span>
              )}
              <span className="scrape-pill-label">
                <span>{pill.label}</span>
                {pill.count > 0 ? <span className="scrape-pill-count">{pill.count}</span> : null}
              </span>
            </button>
          </Tooltip>
        </span>
        <PatientScrapeJobsModal
          open={this.state.open}
          onClose={this.handleClose}
          patientId={this.props.patientId}
          patientName={this.props.patientName}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const ids = (state.scrapeJobs && state.scrapeJobs.idsByPatient[ownProps.patientId]) || [];
  const jobs = ids
    .map(function (id) { return state.scrapeJobs.byId[id]; })
    .filter(Boolean);
  return { jobs };
};

export default connect(mapStateToProps)(TreatmentCardScrapeJobIndicator);
